import React, { useState } from "react";
import { Button, Table } from "react-bootstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

// RTK Query hooks – adjust paths as needed
import { useGetDailyStoreStocksQuery } from "../../slices/dailyStoreStocksApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreTransfersQuery } from "../../slices/storeTransfersApiSlice";
import { useGetProductsQuery } from "../../slices/productsApiSlice";

const DailyStoreStockReport = () => {
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [firstDate, setFirstDate] = useState("");
  const [secondDate, setSecondDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [report, setReport] = useState([]);

  // Fetch data
  const { data: dailyStoreStocksData = { dailyStoreStocks: [] } } = useGetDailyStoreStocksQuery();
  const { data: storeSuppliesData = { storeSupplies: [] } } = useGetStoreSuppliesQuery();
  const { data: storeSalesData = { storeSales: [] } } = useGetStoreSalesQuery();
  const { data: storeTransfersData = { storeTransfers: [] } } = useGetStoreTransfersQuery();
  
  const [pageNumber] = useState(1);
  const { data: productsData = { products: [] } } = useGetProductsQuery({ pageNumber });
  
  // Build the per-product report
  const calculateReport = () => {
    if (!firstDate || !secondDate) {
      toast.error("Please select both Start Date and End Date");
      return;
    }
    const start = new Date(firstDate);
    const end = new Date(secondDate);
    const productMap = {};

    const ensureProduct = (name) => {
      if (!productMap[name]) {
        productMap[name] = {
          name,
          initialDailyStoreStock: 0,
          purchases: 0,
          storeSalesQty: 0,
          storeTransfersQty: 0,
          closingDailyStoreStock: 0,
        };
      }
      return productMap[name];
    };

    // 1) INITIAL STORESTOCK on firstDate
    dailyStoreStocksData.dailyStoreStocks.forEach((dailyStoreStock) => {
      const d = new Date(dailyStoreStock.date).toISOString().split("T")[0];
      if (d === firstDate) {
        dailyStoreStock.dailyStoreStockItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.initialDailyStoreStock += item.qty;
        });
      }
    });

    // 2) PURCHASES in [firstDate, secondDate]
    storeSuppliesData.storeSupplies.forEach((sup) => {
      const d = new Date(sup.date);
      if (d >= start && d < end) {
        sup.storeSupplyItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.purchases += item.qty;
        });
      }
    });

    // 3) STORESTORESALES in [firstDate, secondDate]
    storeSalesData.storeSales.forEach((storeSale) => {
      const d = new Date(storeSale.date);
      if (d >= start && d < end) {
        storeSale.storeSaleItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.storeSalesQty += item.qty;
        });
      }
    });

    // 4) STORETRANSFERS in [firstDate, secondDate]
    storeTransfersData.storeTransfers.forEach((trans) => {
      const d = new Date(trans.date);
      if (d >= start && d < end) {
        trans.storeTransferItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.storeTransfersQty += item.qty;
        });
      }
    });

    // 5) CLOSING STORESTOCK on secondDate (or fallback to latest ≤ secondDate)
    const exact = dailyStoreStocksData.dailyStoreStocks.filter(
      (dailyStoreStock) =>
        new Date(dailyStoreStock.date).toISOString().split("T")[0] === secondDate
    );
    if (exact.length) {
      exact.forEach((dailyStoreStock) =>
        dailyStoreStock.dailyStoreStockItems.forEach((item) => {
          const p = ensureProduct(item.name);
          p.closingDailyStoreStock += item.qty;
        })
      );
    } else {
      // fallback
      let latest = null;
      dailyStoreStocksData.dailyStoreStocks.forEach((dailyStoreStock) => {
        const d = new Date(dailyStoreStock.date);
        if (d <= end && (!latest || d > latest)) latest = d;
      });
      if (latest) {
        dailyStoreStocksData.dailyStoreStocks
          .filter(
            (dailyStoreStock) => new Date(dailyStoreStock.date).getTime() === latest.getTime()
          )
          .forEach((dailyStoreStock) =>
            dailyStoreStock.dailyStoreStockItems.forEach((item) => {
              const p = ensureProduct(item.name);
              p.closingDailyStoreStock += item.qty;
            })
          );
      }
    }

    // 6) Derived fields: price, values, cost & profit
    const out = Object.values(productMap).map((p) => {
      const prod = productsData.products.find((x) => x.name === p.name);
      p.price = prod?.price || 0;
      p.dailyStoreStockValue = p.closingDailyStoreStock * p.price;
      p.balanceQty =
        p.initialDailyStoreStock + p.purchases - p.storeSalesQty - p.storeTransfersQty;
      p.balanceValue = p.balanceQty * p.price;
      p.difference = p.closingDailyStoreStock - p.balanceQty;
      p.diffValue = p.difference * p.price;
      // average cost from storeSupplies
      const totalCost = storeSuppliesData.storeSupplies
        .flatMap((s) => s.storeSupplyItems)
        .filter((i) => i.name === p.name)
        .reduce((sum, i) => sum + i.itemCost, 0);
      const totalQty = p.initialDailyStoreStock + p.purchases;
      p.averageCost = totalQty > 0 ? totalCost / totalQty : 0;
      p.totalCost = p.averageCost * totalQty;

      // profit = revenue – cost
      p.profit = p.storeSalesQty * p.price - p.totalCost;
      return p;
    });

    setReport(out);
  };

  // Grand totals (over the entire report)
  const getGrandTotal = (field) =>
    report.reduce((sum, p) => sum + (p[field] || 0), 0).toFixed(2);

  // Filter & sort for display/export
  let filtered = report
    .filter((p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((p) => {
  switch (filter) {
    case "positive":
      return p.difference > 0;
    case "negative":
      return p.difference < 0;
    case "lowBalance":
      return p.balanceQty < 10;
    case "topPositive":
      return p.difference > 0;
    case "topNegative":
      return p.difference < 0;
    case "opening":
      return p.initialDailyStoreStock > 0;
    case "purchase":
      return p.purchases > 0;
    case "counted":
      return p.closingDailyStoreStock > 0;
    default:
      return true;
  }
});

    if (filter === "topPositive") {
  filtered = filtered
    .sort((a, b) => b.difference - a.difference)
    .slice(0, 10);
} else if (filter === "topNegative") {
  filtered = filtered
    .sort((a, b) => a.difference - b.difference)
    .slice(0, 10);
} else if (filter === "opening") {
  filtered = filtered
    .filter((p) => p.initialDailyStoreStock > 0)
    .sort((a, b) => b.initialDailyStoreStock - a.initialDailyStoreStock)
    .slice(0, 100);
} else if (filter === "purchase") {
  filtered = filtered
    .filter((p) => p.purchases > 0)
    .sort((a, b) => b.purchases - a.purchases)
    .slice(0, 100);
} else if (filter === "counted") {
  filtered = filtered
    .filter((p) => p.closingDailyStoreStock > 0)
    .sort((a, b) => b.closingDailyStoreStock - a.closingDailyStoreStock)
    .slice(0, 100);
}

  // Export to Excel
  const exportToExcel = () => {
    const rows = filtered.map((p) => ({
      Name: p.name,
      Price: p.price.toFixed(2),
      "Initial DailyStoreStock": p.initialDailyStoreStock,
      Purchases: p.purchases,
      StoreSales: p.storeSalesQty,
      StoreTransfers: p.storeTransfersQty,
      "Closing DailyStoreStock": p.closingDailyStoreStock,
      "Balance Qty": p.balanceQty,
      "DailyStoreStock Value": p.dailyStoreStockValue.toFixed(2),
      "Balance Value": p.balanceValue.toFixed(2),
      "Avg Cost": p.averageCost.toFixed(2),
      "Total Cost": p.totalCost.toFixed(2),
      Profit: p.profit.toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyStoreStock Analysis");
    XLSX.writeFile(wb, "DailyStoreStock_Analysis_Report.xlsx");
  };

  return (
    <div className="dailyStoreStock-analysis">
      <h1>DailyMiniStock Analysis</h1>

      {/* Search */}
      <div className="search-box mb-3">
        <input
          type="text"
          placeholder="Search products..."
          className="form-control"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Date Range */}
      <div className="date-range mb-3">
        <label className="me-3">
          Start:
          <input
            type="date"
            className="form-control d-inline-block w-auto ms-2"
            value={firstDate}
            onChange={(e) => setFirstDate(e.target.value)}
          />
        </label>
        <label>
          End:
          <input
            type="date"
            className="form-control d-inline-block w-auto ms-2"
            value={secondDate}
            onChange={(e) => setSecondDate(e.target.value)}
          />
        </label>
      </div>

      {/* Filters */}
<div className="filters mb-3">
  <Button variant="outline-secondary" onClick={() => setFilter("all")}>
    All
  </Button>
  <Button variant="outline-success" onClick={() => setFilter("positive")}>
    Positive
  </Button>
  <Button variant="outline-danger" onClick={() => setFilter("negative")}>
    Negative
  </Button>
  <Button variant="outline-primary" onClick={() => setFilter("topPositive")}>
    Top +ve
  </Button>
  <Button variant="outline-warning" onClick={() => setFilter("topNegative")}>
    Top –ve
  </Button>
  <Button variant="outline-info" onClick={() => setFilter("lowBalance")}>
    Low Bal.
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("opening")}>
    Opening
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("purchase")}>
    Purchase
  </Button>
  <Button variant="outline-dark" onClick={() => setFilter("counted")}>
    Counted
  </Button>
</div>
      {/* Actions */}
      <div className="mb-3">
        <Button variant="primary" className="me-2" onClick={calculateReport}>
          Generate Report
        </Button>
        <Button style={{display: "none"}} variant="success" onClick={exportToExcel}>
          Export to Excel
        </Button>
      </div>

      {/* Summary */}
      {report.length > 0 && (
        <div style={{display: "none"}} className="summary mb-4" >
          <h5>Total DailyStoreStock Value: ${getGrandTotal("dailyStoreStockValue")}</h5>
          <h5>Total Balance Value: ${getGrandTotal("balanceValue")}</h5>
          <h5>Total Difference Value: ${getGrandTotal("diffValue")}</h5>
          <h5>Total Cost: ${getGrandTotal("totalCost")}</h5>
          <h5>Total Profit: ${getGrandTotal("profit")}</h5>
        </div>
      )}

      {/* Data Table */}
      {filtered.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Price</th>
              <th>Initial DailyStoreStock</th>
              <th>Purchases</th>
              <th>StoreSales</th>
              <th>StoreTransfers</th>
              <th>Balance Qty</th>
              <th>Closing DailyStoreStock</th>
              <th>Diff</th>
              <th>Diff Value</th>
              <th>DailyStoreStock Value</th>
              <th>Balance Value</th>
              <th>Avg Cost</th>
              <th>Total Cost</th>
              <th>Profit</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td>${p.price.toFixed(2)}</td>
                <td>{p.initialDailyStoreStock}</td>
                <td>{p.purchases}</td>
                <td>{p.storeSalesQty}</td>
                <td>{p.storeTransfersQty}</td>
                <td>{p.balanceQty}</td>
                <td>{p.closingDailyStoreStock}</td>
                <td>{p.difference}</td>
                <td>{p.diffValue}</td>
                <td>${p.dailyStoreStockValue.toFixed(2)}</td>
                <td>${p.balanceValue.toFixed(2)}</td>
                <td>${p.averageCost.toFixed(2)}</td>
                <td>${p.totalCost.toFixed(2)}</td>
                <td>${p.profit.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default DailyStoreStockReport;
